// import { report_pdf_generate } from '../GA';
import { createHash } from "crypto-browserify";
import { formatProductModelForPdfName } from "../Functions";
import { pagesToExportArray } from "../ScannersBijFunctions";
import { queue } from "./Amplify";
import { getPdfPromotions } from "./PageDataCollector";
import loadModularConfig from "./modular";
import buildDocumentScripts from "./pdf-scripts/hydrate";
const DEFAULT_PDF_NAME = "{{date}}_document.pdf";

// Replace <link> with their <style> contents
function replaceLink(o) {
  const obj = o;
  return new Promise((accept, reject) => {
    fetch(o.href)
      .then(function (res) {
        const reader = res.body.getReader();
        var styles = "";
        const decoder = new TextDecoder();
        function processText({ done, value }) {
          if (done) {
            obj.outerHTML = "<style>" + styles + "</style>";
            accept(styles);
          } else {
            styles += decoder.decode(value);
            reader.read().then(processText);
          }
        }
        reader.read().then(processText);
      })
      .catch(function (e) {
        console.error(e);
        reject(e);
      });
  });
}

function replaceLinks(doc) {
  var allPromises = [];

  // find all 'src'
  doc.querySelectorAll("[src]").forEach(function (o) {
    if (String(o.getAttribute("src")).match(/^https?/)) return;
    // replace src with fully qualified (computed) version
    o.setAttribute("src", o.src);
  });

  // find all links
  doc.querySelectorAll("[href]").forEach(function (o) {
    var href = o.getAttribute("href");
    // skip page-level anchors and links that are already valid
    if (href.match(/^(?:#|https?:\/\/)/)) return;
    // extract content...?

    if (o.tagName !== "LINK") return;
    if (o.getAttribute("rel") !== "stylesheet") {
      if (o.getAttribute("as") !== "style") return;
    }

    allPromises.push(replaceLink(o));
  });

  return Promise.all(allPromises);
}

function replaceEmotionCss(clonedDoc) {
  // replace emotionjs links first
  // source: https://github.com/emotion-js/emotion/issues/1248#issuecomment-494497194

  // "clonedDoc" does not contain "sheet" information, so we check against existing doc only
  var styleElements = [...document.querySelectorAll("style")];
  var styles = styleElements
    .flatMap((element) => {
      let { sheet } = element;
      if (!sheet) {
        if (element.innerHTML.length > 1) {
          return element.innerHTML;
        }
        return "";
      }
      return [...sheet.cssRules].map((rules) => rules.cssText);
    })
    .join("\n")
    .trim();

  // styles insertion "optimized" only in production
  if (!styles) return;

  // Remove all styles from cloned document before applying new style element
  [...clonedDoc.querySelectorAll("style")].forEach((item) => item.remove());

  var combinedStylesContainer = document.createElement("style");
  combinedStylesContainer.type = "text/css";
  combinedStylesContainer.id = "combinedStylesContainer";
  combinedStylesContainer.innerHTML = styles;

  clonedDoc.querySelector("head").appendChild(combinedStylesContainer);
}

// Pseudo-elements and state pseudo-classes cannot be evaluated against a
// static document, so they are removed before a selector is tested and only
// the bare compound selector decides: ".x:hover" survives whenever ".x" is
// present. Brackets and strings are tracked so a ":" inside an attribute
// value is left alone.
function stripPseudoParts(selector) {
  var out = "";
  var i = 0;
  var brackets = 0;
  var quote = null;
  while (i < selector.length) {
    var c = selector[i];
    if (quote) {
      out += c;
      if (c === "\\") out += selector[++i] || "";
      else if (c === quote) quote = null;
      i++;
      continue;
    }
    if (c === '"' || c === "'") {
      quote = c;
      out += c;
      i++;
      continue;
    }
    if (c === "[") brackets++;
    if (c === "]") brackets--;
    if (c === ":" && brackets === 0) {
      i++;
      if (selector[i] === ":") i++;
      while (i < selector.length && /[-\w\\]/.test(selector[i])) i++;
      if (selector[i] === "(") {
        var depth = 0;
        var inner_quote = null;
        while (i < selector.length) {
          var cc = selector[i];
          if (inner_quote) {
            if (cc === "\\") i++;
            else if (cc === inner_quote) inner_quote = null;
          } else if (cc === '"' || cc === "'") inner_quote = cc;
          else if (cc === "(") depth++;
          else if (cc === ")") {
            depth--;
            if (depth === 0) {
              i++;
              break;
            }
          }
          i++;
        }
      }
      continue;
    }
    out += c;
    i++;
  }
  return out;
}

// Stripping a pseudo can leave a dangling or doubled combinator behind
// (".a > :hover" -> ".a > "), which no selector engine accepts.
function tidySelector(selector) {
  var s = selector.replace(/\s+/g, " ").trim();
  while (/[>+~]\s*[>+~]/.test(s)) s = s.replace(/([>+~])\s*[>+~]/g, "$1");
  return s
    .replace(/^\s*[>+~]\s*/, "")
    .replace(/\s*[>+~]\s*$/, "")
    .trim();
}

function splitSelectorList(list) {
  var parts = [];
  var current = "";
  var depth = 0;
  var brackets = 0;
  var quote = null;
  for (var i = 0; i < list.length; i++) {
    var c = list[i];
    if (quote) {
      current += c;
      if (c === "\\") current += list[++i] || "";
      else if (c === quote) quote = null;
      continue;
    }
    if (c === '"' || c === "'") {
      quote = c;
      current += c;
      continue;
    }
    if (c === "(") depth++;
    if (c === ")") depth--;
    if (c === "[") brackets++;
    if (c === "]") brackets--;
    if (c === "," && depth === 0 && brackets === 0) {
      parts.push(current);
      current = "";
      continue;
    }
    current += c;
  }
  parts.push(current);
  return parts;
}

// ":root" cannot be matched from inside the cloned tree, and "html"/"body"/"*"
// rules are where the var() definitions the whole document reads from live
// (--letter-spacing, --margin-*): nothing consumes them by selector, so no
// matching test can prove them unused.
const rootish_selector = /^(?::root|html|body|\*)$/;

// The single-page-export pipeline consumes this export downstream:
// pdf-generator-lambdas' singlePageExport_adapter harvests every <style> from
// the exported bundle and re-parents one of its elements inside
// <div id="html-container" class="sheet spe"> (src/templates/HTMLRenderer.js).
// ".spe" and "#html-container" therefore never match the document being
// exported here, and a plain match test would drop the rules that single-page
// export depends on (".spe .jss1182 .back-to-top-container" et al.). Strip
// those wrapper tokens before testing and let the residual selector decide.
// Stripping only generalizes a selector, so decisions can only flip from drop
// to keep, never the other way. The wrapper's remaining class, ".sheet",
// needs no special case: every sheet in this document carries it already.
const downstream_wrapper_token = /(?:\.spe|#html-container)(?![\w-])/g;

function selectorSurvives(selector_list, scopes) {
  return splitSelectorList(selector_list).some(function (part) {
    var bare = tidySelector(
      stripPseudoParts(part).replace(downstream_wrapper_token, "")
    );
    // Nothing testable left (":root", "::selection", ":where(...)") -> keep.
    if (!bare || rootish_selector.test(bare)) return true;
    return scopes.some(function (scope) {
      try {
        return !!scope.querySelector(bare);
      } catch (e) {
        // A selector this engine will not parse is kept rather than guessed at.
        return true;
      }
    });
  });
}

// @media and @supports carry a numeric type; @container/@layer/@scope do not,
// so they are recognised by class name. Anything else with a block stays
// verbatim.
function isPrunableGroupRule(rule) {
  if (!rule.cssRules) return false;
  if (rule.type === 4 || rule.type === 12) return true;
  var kind = (rule.constructor && rule.constructor.name) || "";
  return /^CSS(?:Container|LayerBlock|Scope)Rule$/.test(kind);
}

function pruneRuleList(rules, scopes) {
  var kept = [];
  for (var i = 0; i < rules.length; i++) {
    var rule = rules[i];

    if (rule.type === 1) {
      if (selectorSurvives(rule.selectorText, scopes)) kept.push(rule.cssText);
      continue;
    }

    if (isPrunableGroupRule(rule)) {
      var inner = pruneRuleList(rule.cssRules, scopes);
      if (inner.length) {
        var header = rule.cssText.slice(0, rule.cssText.indexOf("{"));
        kept.push(header + "{\n" + inner.join("\n") + "\n}");
      }
      continue;
    }

    // @font-face, @page, @keyframes, @property and any rule kind this engine
    // reports as something else: no static test can prove them unused.
    kept.push(rule.cssText);
  }
  return kept;
}

const statement_at_rule = /^@[-\w]+[^{]*;\s*$/;

// Statement at-rules (@charset, @import, @layer names, @namespace) are only
// valid ahead of the first style rule, so a CSSOM parse silently discards the
// ones replaceEmotionCss's flatten leaves buried mid-block — including the
// fast.fonts.net @import that supplies the Epson brand faces, which today
// survives only because EvoPDF's parser is more forgiving than the spec. Read
// them out of the raw text instead and put them back where they are valid.
function statementAtRules(css) {
  var found = [];
  var depth = 0;
  var i = 0;
  while (i < css.length) {
    var c = css[i];
    if (c === "/" && css[i + 1] === "*") {
      var close = css.indexOf("*/", i + 2);
      i = close === -1 ? css.length : close + 2;
      continue;
    }
    if (c === '"' || c === "'") {
      i++;
      while (i < css.length && css[i] !== c) i += css[i] === "\\" ? 2 : 1;
      i++;
      continue;
    }
    if (c === "{") depth++;
    else if (c === "}") depth--;
    else if (c === "@" && depth === 0) {
      var end = i;
      while (end < css.length && css[end] !== ";" && css[end] !== "{") {
        if (css[end] === '"' || css[end] === "'") {
          var quote = css[end++];
          while (end < css.length && css[end] !== quote)
            end += css[end] === "\\" ? 2 : 1;
        }
        end++;
      }
      if (css[end] === ";") {
        found.push(css.slice(i, end + 1).replace(/\s+/g, " "));
        i = end + 1;
        continue;
      }
    }
    i++;
  }
  return found;
}

// The exported markup inlines every same-origin stylesheet in full — main.css
// alone is ~690 KB, almost all of it @radix-ui/themes that the PDF templates
// never use. Drop the rules that cannot apply to the document being exported.
function pruneUnmatchedCss(clonedDoc) {
  try {
    // Matching needs a real document: clonedDoc is a detached <html> element,
    // so on its own it answers nothing for ":root" or for any selector
    // anchored at the root element (".export .foo" is added right here in
    // cleanupHtmlForExport). Lend it to a scratch document, then take it back.
    var owner = clonedDoc.ownerDocument;
    var probe_doc = document.implementation.createHTMLDocument("");
    probe_doc.replaceChild(
      probe_doc.adoptNode(clonedDoc),
      probe_doc.documentElement
    );
    try {
      [...clonedDoc.querySelectorAll("style")].forEach(function (style) {
        var css = style.textContent;
        if (!css || !css.trim()) return;
        // A <style> exposes .cssRules only once it belongs to a document, and
        // these were built detached — parse through a throwaway element.
        var parser = probe_doc.createElement("style");
        parser.textContent = css;
        (probe_doc.head || clonedDoc).appendChild(parser);
        try {
          var sheet = parser.sheet;
          if (!sheet) return;
          // The live document is a scope too: submitHtml still injects live
          // markup into the clone after this runs (bij-energy replaces <main>
          // with .reportContainer), and those rules would be gone by then.
          // Costs ~3 KB, and the extras match nothing in the exported
          // document, so they cannot change how it renders.
          // A cross-origin sheet throws on .cssRules; the catch below leaves
          // its text verbatim.
          var kept = pruneRuleList(sheet.cssRules, [
            probe_doc,
            document
          ]).filter(function (text) {
            return !statement_at_rule.test(text);
          });
          style.textContent = statementAtRules(css).concat(kept).join("\n");
        } catch (e) {
          console.error(e);
        } finally {
          parser.remove();
        }
      });
    } finally {
      owner.adoptNode(clonedDoc);
    }
  } catch (e) {
    // An export must never fail because pruning failed.
    console.error("Export CSS pruning skipped: " + e);
  }
}

function removeUnexpectedRootSiblings(clonedDoc) {
  const root = clonedDoc.querySelector("#root");
  let current = root.nextSibling;
  let siblings = [];
  while (current) {
    siblings.push(current);
    current = current.nextSibling;
  }
  siblings.forEach((sibling) => sibling.remove());
}

function cleanupHtmlForExport() {
  var clonedDoc = document.documentElement.cloneNode(true);
  // add "export" class to HTML with intention of applying evopdf fixes (if any)
  clonedDoc.classList.add("export");

  // remove scripts and preview-only items
  var toRemove = clonedDoc.querySelectorAll(
    ".removeForExport,script,[data-meta*=removeForExport]"
  );
  toRemove.forEach((e) => {
    e.remove();
  });

  removeUnexpectedRootSiblings(clonedDoc);

  replaceEmotionCss(clonedDoc);
  return replaceLinks(clonedDoc).then((res) => {
    // all promises complete
    setTableCellHeights(clonedDoc);
    clearDropdowns(clonedDoc);
    clearStylesForExport(clonedDoc);
    clearClasses(clonedDoc);
    includeInputs(clonedDoc);
    replaceHrefs(clonedDoc);
    setFixHeightsForExport(clonedDoc);
    // Last: every step above changes what is in the document, and pruning
    // decides by what is in it.
    if (!process.env.REACT_APP_DISABLE_CSS_PRUNE) pruneUnmatchedCss(clonedDoc);
    return clonedDoc;
  });
}

function replaceHrefs(clonedDoc) {
  [...clonedDoc.querySelectorAll("[data-href]")].forEach((e) => {
    e.href = e.getAttribute("data-href");
  });
}

const attr_clear_classes = "data-clear-classes";
function clearClasses(clonedDoc) {
  [...clonedDoc.querySelectorAll(`[${attr_clear_classes}]`)].forEach((e) => {
    const classes = e.getAttribute(attr_clear_classes).split(",");
    e.classList.remove(...classes);
    e.removeAttribute(attr_clear_classes);
  });
}
function clearStylesForExport(clonedDoc) {
  [...clonedDoc.getElementsByClassName("clearStylesForExport")].forEach((e) => {
    e.classList.remove("clearStylesForExport");
    e.style = "";
  });
}

function elementIsNestedInRemovableSection(table) {
  let el = table;
  while (el) {
    // Document node has no classList
    if (!el.classList) return false;
    if (el.classList.contains("removeForExport")) return true;
    el = el.parentNode;
  }
  return false;
}

function setTableCellHeights(clonedDoc) {
  const tables = [...document.querySelectorAll("table")]
    // Remove tables which are nested under some ".removeForExport'"
    .filter((t) => !elementIsNestedInRemovableSection(t));

  const cell_selector = "th,td";

  // collect heights from visible cells
  const visible_cells = tables.flatMap((t) => [
    ...t.querySelectorAll(cell_selector)
  ]);
  var cell_heights = visible_cells.map((cell) => cell.offsetHeight + "px");

  // apply heights from visible cells onto cloned document cells
  const cloned_cells = [...clonedDoc.querySelectorAll(cell_selector)];
  cloned_cells.forEach((cell, idx) => (cell.style.height = cell_heights[idx]));
}

function clearDropdowns(clonedDoc) {
  [...clonedDoc.querySelectorAll("select")].forEach(
    (e) => (e.selectedIndex = 0)
  );
}

function includeInputs(clonedDoc) {
  // Convert to inputs
  [...clonedDoc.querySelectorAll(".convertToInput")].forEach(
    (parent_container, idx) => {
      let new_input = document.createElement("textarea");
      new_input.rows = 2;

      parent_container.insertBefore(new_input, parent_container.firstChild);
      new_input = null;
    }
  );

  // Set title names appropriately
  [
    ...clonedDoc.querySelectorAll(".convertToInput.product-title textarea")
  ].forEach((input, idx) => {
    input.name = `qc_title_${idx}`;
  });
  // Set feature cell names appropriately
  [...clonedDoc.querySelectorAll(".comparison-table .tableColumn")].forEach(
    (column, column_idx) => {
      [...column.querySelectorAll(".convertToInput textarea")].forEach(
        (input, input_idx) => {
          input.name = `qc_feature_${column_idx}_${input_idx}`;
        }
      );
    }
  );
}

/**
 * elementIsNestedInRemovableSection
 */
function setFixHeightsForExport(clonedDoc) {
  const visible_elements = [
    ...document.querySelectorAll(".setFixedHeightForExport")
  ]
    // Remove tables which are nested under some ".removeForExport'"
    .filter((t) => !elementIsNestedInRemovableSection(t));

  const invisible_elements = [
    ...clonedDoc.querySelectorAll(".setFixedHeightForExport")
  ];

  if (invisible_elements.length !== visible_elements.length) {
    const message =
      "Unable to fix heights for .setFixedHeightForExport elements due to element count mismatches";
    const mismatch = `${invisible_elements.length} != ${visible_elements.length}`;
    console.error(`${message}: ${mismatch}`);
    return;
  }

  invisible_elements.forEach((invisible_element, element_idx) => {
    const visible_element = visible_elements[element_idx];
    invisible_element.style.height = `${visible_element.offsetHeight}px`;
  });
}

function zeroPad(str, digits) {
  if (str.length < digits)
    for (var i = str.length; i < digits; ++i) str = "0" + str;
  return str;
}
export function dateForPdfName() {
  var d = new Date();
  var year = d.getFullYear();
  var month = "" + (d.getUTCMonth() + 1);
  var day = "" + d.getUTCDate();
  return [year, zeroPad(month, 2), zeroPad(day, 2)].join("-");
}

export function localDateForPdfName() {
  var d = new Date();
  var year = d.getFullYear();
  var month = "" + (d.getMonth() + 1);
  var day = "" + d.getDate();
  return [zeroPad(day, 2), zeroPad(month, 2), year].join("-");
}

// returns a list of matched variables
function variablesFromTemplate(template) {
  if (!template) return [];
  var matches = [...template.matchAll(/{{(.*?)}}/g)];
  return matches.map((match) => {
    return {
      variable: match[1],
      to_replace: match[0]
    };
  });
}

function renderTemplate(str, variables) {
  var tpl_vars = variablesFromTemplate(str);
  tpl_vars.forEach((tpl_var) => {
    var value = variables[tpl_var.variable] || "";
    var regexp = new RegExp(tpl_var.to_replace, "g");
    str = str.replace(regexp, value);
  });
  return str;
}

function formatPdfName(givenFromProject, projectType, variablesOverrides = {}) {
  var variables = {
    date: dateForPdfName(),
    ...variablesOverrides
  };

  let name = givenFromProject || DEFAULT_PDF_NAME;
  //to avoid converting .pdf to -pdf
  if (name.endsWith(".pdf")) name = name.slice(0, -4);
  if (["pos", "latin-pos"].includes(projectType)) name = `{{date}}_${name}`;
  const renderedName = renderTemplate(name, variables);
  const formattedName = renderedName.toLowerCase().replace(/[^\w]+/g, "-");
  return `${formattedName}.pdf`;
}

// Optional local pdf-generator bypass for development. When set, the bundle
// is POSTed directly to the generator instead of being queued via S3.
const localPdfGeneratorUrl = (
  process.env.REACT_APP_PDF_GENERATOR_URL || ""
).trim();

// Local stand-in for the queue lambda's pdfGeneratorReceiver/util.js ->
// createConfigFileName. The normal flow uploads the submission to S3 under this key
// and hands it to the generator, which embeds it in the landing / single-page-export
// URLs. The local bypass skips S3, so we derive an equivalent key here; otherwise the
// generator receives an empty {s3Key} and the URLs come out with a double slash
// (".../landing//<ids>", ".../spe/<type>//<sheet>"). The value doesn't need to match
// the lambda's (we skip bundleVerify in local mode), so we hash the same shape with
// md5 instead of sha256 -- crypto-browserify's sha256 path goes through cipher-base,
// which is broken under CRA's webpack 5 polyfills, whereas the md5 path works.
function deriveBundleS3Key(submissionBody) {
  const other = submissionBody.other || {};
  const backgroundSuffix = other.backgroundExport ? "batch-bg/" : "";

  // Background batch slices key off explicit params (matches the lambda)
  const { parentBundle, sliceId } = other;
  if (parentBundle && sliceId)
    return backgroundSuffix + `${parentBundle}_${sliceId}`;

  const { hashKeys = [], dbDataRaw = {} } = submissionBody.hashConfig || {};
  const hasHashConfig =
    typeof dbDataRaw === "object" &&
    Object.keys(dbDataRaw).length > 0 &&
    Array.isArray(hashKeys) &&
    hashKeys.length > 0;

  const dataToHash = hasHashConfig
    ? {
        dbDataRaw,
        ...hashKeys.reduce(
          (acc, key) => ({ ...acc, [key]: submissionBody[key] }),
          {}
        )
      }
    : submissionBody;

  return (
    backgroundSuffix +
    createHash("md5").update(JSON.stringify(dataToHash)).digest("hex")
  );
}

function submitHtml_local(submissionBody) {
  // The generator NPEs when "other" is missing
  if (!submissionBody.other) submissionBody.other = {};
  // The queued flow assigns this key server-side (when uploading to S3); the local
  // bypass skips that step, so derive the same key here to keep generated URLs valid.
  if (!submissionBody.s3Key)
    submissionBody.s3Key = deriveBundleS3Key(submissionBody);
  const base = localPdfGeneratorUrl.replace(/\/+$/, "");
  // No timeout: conversion is synchronous and can take 30-120 seconds
  return fetch(`${base}/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(submissionBody)
  }).then((response) => {
    if (!response.ok) {
      return response.text().then((text) => {
        throw Error(
          text || `Local PDF generator responded with HTTP ${response.status}`
        );
      });
    }
    return response
      .blob()
      .then((blob) => ({ localPdfUrl: URL.createObjectURL(blob) }));
  });
}

function submitHtml_poll(submissionBody) {
  // Continuously poll
  // Submit HTML
  return queue(submissionBody);
  // .then((queueResponse) => {
  //   var data = JSON.parse(queueResponse.body);
  //   var s3Key = data.s3Key;
  //   // Begin polling
  //   _poll(s3Key);
  // }, (e) => {
  //   console.error(e);
  //   report_pdf_error(e);
  //   setState_error(e);
  // });
}

/**
 * "Push" approach
 */
// submitHtml_push(submissionBody) {
//   var promise = requestFirebaseToken();
//   promise.then((token) => {
//     if (!token) {
//       // error
//       return;
//     }

//     return subscribe(token);
//   }).then((subscriptionResponse) => {
//     var data = subscriptionResponse.body;
//     subscriptionData.EndpointArn = data.endpoint;
//     subscriptionData.SubscriptionArn = data.subscription;
//     return subscriptionData;
//   }).then((sub) => {
//     // queue, then unsubscribe
//     return queue(submissionBody);
//   }).then((queueResponse) => {
//     // unsubscription occurs once notification is received
//     //unsubscribe(subscription);
//   });
// }

function getElementDimensions(element) {
  let styles = window.getComputedStyle(element);
  return {
    width: parseInt(styles.width),
    height: parseInt(styles.height)
  };
}

function getFirstSheetDimensions() {
  let firstSheet = document.querySelector(".sheet");
  let dimensions = getElementDimensions(firstSheet);
  return {
    sheetWidth: dimensions.width,
    sheetHeight: dimensions.height
  };
}

function removeSiblings(element, siblingKey) {
  while (!!element) {
    const sibling_to_remove = element;
    element = element[siblingKey];
    sibling_to_remove.remove();
  }
}

function isolateSheet(clonedDoc, element_id) {
  const sheet_id = "#" + element_id;
  const target_element = clonedDoc.querySelector(sheet_id);
  if (!target_element) {
    throw Error('Unable to export: target sheet "' + sheet_id + '" missing.');
  }

  // Remove all previous siblings
  removeSiblings(target_element.previousSibling, "previousSibling");
  // Remove all subsequent siblings
  removeSiblings(target_element.nextSibling, "nextSibling");
}

function getForcePdfExport(clonedDoc) {
  var targetElement = clonedDoc.querySelector("[force-pdf-export]");
  if (targetElement) {
    targetElement.removeAttribute("force-pdf-export");
    return true;
  }
  return false;
}

const defaultHashKeys = [
  "other",
  "type",
  "sheetWidth",
  "sheetHeight",
  "referrer"
];

// Fields whose only consumer is the generator's legacy script assembly —
// the frontend hydrates them into documentScripts, so exports that carry
// documentScripts do not send them (PdfGenerator.java reads them nowhere
// else: every use sits in the legacy path the generic one bypasses;
// verified 2026-08-31). Exports without documentScripts (pos, bij-energy,
// single-page) never had these fields, and the v8 comparisontool has its
// own client — both unaffected.
const SCRIPT_TEMPLATE_FIELDS = [
  "quickCompareMapData",
  "mappedCompetingModels",
  "visibilityOptions",
  "recModelsPages",
  "productFilterNames",
  "findByFeaturesConfig"
];

function submitHtml(
  clonedDoc,
  pdfExportData,
  projectType,
  selectedContact,
  selectedLocale,
  forcedExport,
  exposeContent,
  pagesToExport,
  emailTo
) {
  // Remove siblings of the Page element to export, so that it is the only page present
  const { idOfSinglePageElementToExport, isStandaloneSinglePage, other } =
    pdfExportData;
  if (idOfSinglePageElementToExport) {
    isolateSheet(clonedDoc, idOfSinglePageElementToExport);
  }
  const variablesOverrides = {};
  if (projectType === "bij-energy") {
    variablesOverrides.template = other.templateType;
    variablesOverrides.client = other.client || "";
  }
  const pdfName = formatPdfName(
    pdfExportData.name,
    projectType,
    variablesOverrides
  );
  const isSinglePage =
    isStandaloneSinglePage || !!idOfSinglePageElementToExport; //document.querySelectorAll('.sheet').length === 1 ||
  var additionalInfoForExporter = isSinglePage
    ? {}
    : pdfExportData.additional || {};

  if (additionalInfoForExporter.allPdfPromoLinks === true)
    additionalInfoForExporter.allPdfPromoLinks = getPdfPromotions(clonedDoc);

  const { dbDataRaw, ...additionalData } = additionalInfoForExporter;
  //TODO
  if (projectType === "bij-energy") {
    const main = clonedDoc.querySelector("main");
    const reportContainer = document.querySelector(".reportContainer");
    main.innerHTML = reportContainer.innerHTML;
    const reportIds = main.querySelectorAll(".report-id-value");
    reportIds.forEach((reportId) => {
      reportId.innerText = other.reportId || "xxxx";
    });
  }
  var html = clonedDoc.outerHTML;
  var hashKeys = [...defaultHashKeys, ...Object.keys(additionalData || {})];
  var firstSheetDimensions = getFirstSheetDimensions();
  const forceExport = getForcePdfExport(clonedDoc) || forcedExport;
  const isPos = ["pos", "latin-pos"].includes(projectType);
  // The generator runs JPE (an iText pass: license fetch, volume-licensed
  // use, PDF rewrite) whenever editorConfig is non-null — see pdf-generator
  // Main.java `if (editorConfig != null)`. Projects without a modular config
  // (POS, static exports, no collections) must therefore send null, not {}.
  const loaded_modular_config =
    !exposeContent && !isPos
      ? loadModularConfig(pdfExportData.all_data?.collections)
      : null;
  const modular_config =
    loaded_modular_config && Object.keys(loaded_modular_config).length > 0
      ? loaded_modular_config
      : null;
  const allPagesToExport = pagesToExportArray(pagesToExport);

  const generateComparisonName = (page) => {
    const all_products = [page.primary_product, ...page.secondary_products];
    const formatted_product_names = all_products.map((p) =>
      formatProductModelForPdfName(p)
    );
    const comparison_name = formatted_product_names.join("-");
    const date = dateForPdfName();
    return `${comparison_name}-comparison_${date}.pdf`;
  };

  const selectedComparisonPages = allPagesToExport.map((page) => ({
    id: page.id,
    name: generateComparisonName(page)
  }));
  const backgroundExport = selectedComparisonPages.length > 0;

  const backgroundExportProps = backgroundExport
    ? {
        selectedComparisonPages,
        emailTo,
        backgroundExport
      }
    : {};

  // Fully hydrated PDF-side document scripts, in attach order (see
  // pdf-scripts/hydrate.js for the attach conditions and the token
  // contract). The generator attaches these verbatim — substituting only
  // the four server-known tokens {s3Key}, {referrer}, {pageCounts} and
  // {totalAppendedPdfPages} — instead of assembling scripts from its
  // embedded resources.
  const documentScripts = buildDocumentScripts(
    projectType,
    additionalData,
    selectedLocale?.language
  );

  // With the scripts travelling hydrated, the template fields stay home and
  // the scripts join the cache key in their place (owner, 2026-08-31):
  // dbDataRaw already carries the data variance, and hashing
  // documentScripts lets script edits reach new exports instead of being
  // masked by the dedup cache.
  let documentScriptsProps = {};
  if (documentScripts.length > 0) {
    documentScriptsProps = { documentScripts };
    SCRIPT_TEMPLATE_FIELDS.forEach((key) => delete additionalData[key]);
    hashKeys = [
      ...defaultHashKeys,
      ...Object.keys(additionalData || {}),
      "documentScripts"
    ];
  }

  var submissionBody = {
    html: html,
    pdfName: pdfName,
    // The generator passes this to EvoPDF as the conversion base URL, and
    // the render server NAVIGATES to it — so a localhost origin makes remote
    // renders die on navigation timeout. Set REACT_APP_EXPORT_REFERRER to a
    // public origin (no path: the PDF's landing/quick-compare links append
    // /<project>/...) when developing locally against remote render infra.
    referrer: process.env.REACT_APP_EXPORT_REFERRER || window.location.origin,
    type: projectType,
    ...firstSheetDimensions,
    ...additionalData,
    ...documentScriptsProps,
    // s3Key: 'test',
    other: {
      // productDetailsId,
      staticExport: exposeContent || isSinglePage,
      pageTitle: pdfExportData?.page_title || "",
      locale: selectedLocale?.language,
      ...(other || {}),
      selectedComparisonPages,
      ...backgroundExportProps
    },

    // Do not include this object unless necessary
    ...(forceExport
      ? {
          receiverConfig: {
            forceExport
          }
        }
      : {}),

    hashConfig: {
      hashKeys,
      dbDataRaw: {
        // Create an MD5 hash of the data
        md5: createHash("md5")
          .update(
            JSON.stringify({
              // Append selected contact to hashed content
              ...dbDataRaw,
              selected_contact: selectedContact?.id,

              // Append single page export information
              ...(!!idOfSinglePageElementToExport
                ? { idOfSinglePageElementToExport }
                : {})
            })
          )
          .digest("hex")
      }
    },

    editorConfig: modular_config
  };

  // Support interrupting export for faster local development.
  // For example, when making changes to the back-end locally.
  if (process.env.REACT_APP_INTERRUPT_EXPORT) {
    console.log("EvoPDF + JPE", submissionBody);
    const { editorConfig, ...remainingData } = submissionBody;
    console.log("EvoPDF", remainingData);
    console.log("JPE", editorConfig);
    // throw Error("DEBUG: Purposeful export cancellation.");
  }

  // Google Analytics
  // report_pdf_generate(projectType);
  if (localPdfGeneratorUrl) return submitHtml_local(submissionBody);
  return submitHtml_poll(submissionBody);
}

export { cleanupHtmlForExport, formatPdfName, submitHtml };
